import React, { useState, useEffect, useRef } from 'react';
import ApiService from '../../../services/apiService';
import { toast } from 'react-toastify';

const UpdateItemGridGRN = ({
    onRequisitionData,
    itemData,
    grnData,
    selectedPOData,
    currencyPrecision,
    setItemData,
    setOverallDiscount,
    grandTotal,
    setVatAmount,
    vatAmount,
    overallDiscount,
    setDiscountAmount,
    discountAmount,
    setGrandTotal,
    exchangeRate = 1,
}) => {
    const [rows, setRows] = useState([]);

    const [overallDiscountAmount, setOverallDiscountAmount] = useState(0);

    const loadedKeyRef = useRef('');

    const [columnWidths, setColumnWidths] = useState({
        srNo: 70,
        itemNo: 100,
        description: 180,
        specification: 150,
        qty: 70,
        uom: 130,
        inventoryQty: 130,
        balQty: 80,
        freeQty: 100,
        unitPrice: 120,
        discount: 100,
        calculatedAmount: 100,
        vatDesc: 100,
        vat: 80,
        vatAmount: 120,
        amount: 100,
        batchNo: 120,
        expDate: 130,
    });

    const precision = Number(currencyPrecision ?? 3);

    const getValidExchangeRate = () => {
        const rate = Number(exchangeRate);

        return Number.isFinite(rate) && rate > 0 ? rate : 1;
    };

    const roundAmount = (value) => {
        const number = Number(value) || 0;
        return Number(number.toFixed(precision));
    };

    const truncateAmount = (value, decimals = precision) => {
        const number = Number(value) || 0;
        const factor = Math.pow(10, decimals);

        return Math.trunc(number * factor) / factor;
    };

    /*
     * Calculates one row using the existing GRN fields.
     * originalUnitPrice and originalDiscountBase remain in the
     * original currency so exchange-rate changes do not compound.
     */
    const calculateRowAmounts = (row, unitPrice, rate = getValidExchangeRate()) => {
        const qty = Number(row.qty) || 0;
        const vatRate = Number(row.vat) || 0;
        const balanceQty = Number(row.balQty) || 0;

        const originalPrice = Number.isFinite(Number(row.originalUnitPrice))
            ? Number(row.originalUnitPrice)
            : (Number(unitPrice) || 0) / rate;

        const originalDiscountBase = Number.isFinite(
            Number(row.originalDiscountBase)
        )
            ? Number(row.originalDiscountBase)
            : (Number(row.originalDiscount) || 0) / rate;

        const currentOriginalDiscount = originalDiscountBase * rate;

        const discount = balanceQty > 0
            ? (currentOriginalDiscount * qty) / balanceQty
            : 0;

        const grossAmount = qty * (Number(unitPrice) || 0);
        const calculatedAmount = grossAmount - discount;
        const calculatedVat = calculatedAmount * vatRate / 100;
        const finalAmount = calculatedAmount + calculatedVat;

        return {
            ...row,
            originalUnitPrice: originalPrice,
            originalDiscountBase,
            originalDiscount: currentOriginalDiscount,
            baseAmount: (Number(unitPrice) || 0).toFixed(4),
            discount: roundAmount(discount).toFixed(precision),
            calculatedAmount: roundAmount(calculatedAmount),
            vatAmount: roundAmount(calculatedVat).toFixed(precision),
            amount: roundAmount(finalAmount),
        };
    };

    const syncRows = (updatedRows) => {
        setRows(updatedRows);

        if (typeof setItemData === 'function') {
            setItemData(updatedRows);
        }

        if (typeof onRequisitionData === 'function') {
            onRequisitionData(updatedRows);
        }
    };

    const calculatedTotal = rows.reduce((sum, row) => {
        const amount = Number(row?.amount) || 0;
        const rowVatAmount = Number(row?.vatAmount) || 0;
        const discount = Number(row?.discount) || 0;

        const final =
            Number(overallDiscount) === 0
                ? amount + discount - rowVatAmount
                : amount - rowVatAmount;

        return sum + final;
    }, 0);

    const totalItemDiscount = rows.reduce(
        (sum, row) => sum + (Number(row?.discount) || 0),
        0
    );

    /*
     * Totals: keep the original overall-discount calculation structure.
     */
    useEffect(() => {
        const overallRate = Number(overallDiscount) || 0;

        const overallAmount = rows.reduce(
            (sum, row) =>
                sum +
                (Number(row?.actualqty) || 0) *
                (Number(row?.baseAmount) || 0),
            0
        );

        const totalQty = rows.reduce(
            (sum, row) => sum + (Number(row?.actualqty) || 0),
            0
        );

        const qty = rows.reduce(
            (sum, row) => sum + (Number(row?.qty) || 0),
            0
        );

        const discountAmountByPercent =
            (overallAmount * overallRate) / 100;

        const discountPerQty =
            totalQty > 0 ? discountAmountByPercent / totalQty : 0;

        const discountAmt =
            overallRate === 0
                ? totalItemDiscount
                : qty * discountPerQty;

        const amountAfterDiscount = calculatedTotal - discountAmt;

        let totalVat = 0;

        if (rows.length === 1) {
            const vatRate = Number(rows[0]?.vat) || 0;
            totalVat = (amountAfterDiscount * vatRate) / 100;
        } else {
            const uniqueVatRates = [
                ...new Set(rows.map((row) => Number(row?.vat) || 0)),
            ];

            if (uniqueVatRates.length === 1) {
                totalVat =
                    (amountAfterDiscount * uniqueVatRates[0]) / 100;
            } else {
                totalVat = rows.reduce(
                    (sum, row) => sum + (Number(row?.vatAmount) || 0),
                    0
                );
            }
        }

        const finalGrandTotal = amountAfterDiscount + totalVat;

        setDiscountAmount(discountAmt);
        setOverallDiscountAmount(discountAmountByPercent);
        setVatAmount(totalVat);
        setGrandTotal(finalGrandTotal);
    }, [
        rows,
        overallDiscount,
        totalItemDiscount,
        calculatedTotal,
        setDiscountAmount,
        setVatAmount,
        setGrandTotal,
    ]);

    /*
     * Load GRN rows.
     * The original unit price is stored using the saved exchange rate.
     */
    useEffect(() => {
        let cancelled = false;

        const getItems = async () => {
            if (!Array.isArray(itemData)) return;

            const grnNo = grnData?.grnNo ?? '';
            const loadKey = `${grnNo}-${itemData.length}`;

            // Prevent refetching and overwriting edits when the parent
            // updates itemData with the same number of rows.
            if (loadedKeyRef.current === loadKey) return;

            try {
                const apiService = new ApiService();

                const param =
                    `?POrderNo=View?POrderNo=View&GRNNo=${grnNo}&Mode=2`;

                const PO = await apiService.getApiParamater(
                    'GET_PO_ITEM_UPDATE',
                    param
                );

                const savedRate =
                    Number(grnData?.exchangeRate) > 0
                        ? Number(grnData.exchangeRate)
                        : getValidExchangeRate();

                const formattedRows = await Promise.all(
                    itemData.map(async (detail) => {
                        const selectedItemsResponse =
                            await apiService.getApiParamater(
                                'GET_ITEM',
                                `?ItemNo=${detail.itemCode}`
                            );

                        const glAccountsResponse =
                            await apiService.getApiParamater(
                                'GET_ITEM_GL',
                                `?ItemNo=${detail.itemCode}`
                            );

                        const poRows = PO?.data?.result || [];

                        const selectedGrnItem = poRows.find(
                            (rec) =>
                                rec.itemDescription === detail.itemDescription
                        );

                        const selectedPOItem =
                            selectedPOData?.poDetails?.find(
                                (rec) =>
                                    rec.itemDescription === detail.itemDescription
                            );

                        const glAccount =
                            glAccountsResponse?.data?.result?.[0] || {};

                        const itm =
                            selectedItemsResponse?.data?.result?.[0] || {};

                        const actualPOQty =
                            Number(selectedGrnItem?.actualPOQty) || 0;

                        const balanceQty =
                            Number(selectedGrnItem?.balQty) || 0;

                        const poDiscount =
                            Number(selectedPOItem?.discount) || 0;

                        const discountPerUnit =
                            actualPOQty > 0
                                ? poDiscount / actualPOQty
                                : 0;

                        const discount =
                            discountPerUnit * (Number(detail.qty) || 0);

                        const originalDiscount =
                            discountPerUnit * balanceQty;

                        const savedUnitPrice =
                            Number(detail.unitPrice) || 0;

                        const originalUnitPrice =
                            savedUnitPrice / savedRate;

                        const originalDiscountBase =
                            originalDiscount / savedRate;

                        const baseRow = {
                            requisitionNo: detail.requisitionNo,
                            srno: detail?.srno,
                            recordId: detail?.recordId,
                            ItemNo: detail.itemCode,
                            qty: Number(detail.qty) || 0,
                            itemDescription: detail.itemDescription,
                            uom: detail.uom,
                            baseQty:
                                (Number(itm?.convToInvUOM) || 0) *
                                (Number(detail.qty) || 0),
                            baseAmount: savedUnitPrice.toFixed(4),
                            originalUnitPrice,
                            vat: Number(detail.vatPerc) || 0,
                            discount: roundAmount(discount).toFixed(precision),
                            originalDiscount,
                            originalDiscountBase,
                            vatDes: detail.vatDesc,
                            vatCode: detail?.vatCode,
                            convValue: Number(itm?.convToInvUOM) || 0,
                            vatAmount: Number(detail.vatAmount || 0).toFixed(
                                precision
                            ),
                            freeQty: Number(detail?.extraQty) || 0,
                            specification: detail?.specification || '',
                            amount: Number(detail?.amount || 0).toFixed(
                                precision
                            ),
                            balQty: balanceQty,
                            calculatedAmount: 0,
                            expDate: detail?.expDate || null,
                            batchNo: detail?.batchNo || '',
                            inventoryAccountCode: glAccount?.inventoryAccountCode,
                            inventoryAccountName: glAccount?.inventoryAccountName,
                            GRNAccountName: glAccount?.grnAccountName,
                            GRNAccountCode: glAccount?.grnAccountCode,
                            actualqty: actualPOQty,
                        };

                        const row = calculateRowAmounts(
                            baseRow,
                            savedUnitPrice,
                            savedRate
                        );

                        // Keep the original currency values separate from
                        // displayed values.
                        return {
                            ...row,
                            originalUnitPrice,
                            originalDiscountBase,
                        };
                    })
                );

                if (cancelled) return;

                loadedKeyRef.current = loadKey;

                if (formattedRows.length > 0) {
                    setRows(formattedRows);

                    if (typeof onRequisitionData === 'function') {
                        onRequisitionData(formattedRows);
                    }
                } else {
                    setRows([]);
                }
            } catch (error) {
                console.error('Error fetching items:', error);
                toast.error('Unable to load GRN items.');
            }
        };

        if (Array.isArray(itemData)) {
            getItems();
        }

        return () => {
            cancelled = true;
        };
        // Loading is keyed to GRN identity and item count. The child updates
        // the parent item array during editing, so it must not refetch on
        // every unit-price or quantity edit.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [grnData?.grnNo, Array.isArray(itemData) ? itemData.length : 0]);

    /*
     * Recalculate displayed prices when the exchange rate changes.
     * Each price is derived from originalUnitPrice, not the last displayed
     * price, preventing repeated multiplication.
     */
    useEffect(() => {
        if (!rows.length) return;

        const rate = getValidExchangeRate();

        const updatedRows = rows.map((row) => {
            const originalPrice = Number(row.originalUnitPrice);

            if (!Number.isFinite(originalPrice)) {
                return row;
            }

            const convertedPrice = originalPrice * rate;

            const originalDiscountBase =
                Number(row.originalDiscountBase) ||
                (Number(row.originalDiscount) || 0) /
                    (Number(grnData?.exchangeRate) > 0
                        ? Number(grnData.exchangeRate)
                        : rate);

            const updatedRow = {
                ...row,
                originalUnitPrice: originalPrice,
                originalDiscountBase,
            };

            return calculateRowAmounts(
                updatedRow,
                convertedPrice,
                rate
            );
        });

        const changed = updatedRows.some((row, index) => {
            const oldRow = rows[index];

            return (
                Math.abs(
                    (Number(row.baseAmount) || 0) -
                    (Number(oldRow.baseAmount) || 0)
                ) > 0.000001 ||
                Math.abs(
                    (Number(row.discount) || 0) -
                    (Number(oldRow.discount) || 0)
                ) > 0.000001 ||
                Math.abs(
                    (Number(row.amount) || 0) -
                    (Number(oldRow.amount) || 0)
                ) > 0.000001
            );
        });

        if (changed) {
            syncRows(updatedRows);
        }
        // Deliberately run when the exchange rate changes, not on each row edit.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exchangeRate]);

    const ResizableHeader = ({ column, children }) => (
        <th
            style={{
                width: `${columnWidths[column]}px`,
                minWidth: `${columnWidths[column]}px`,
                position: 'relative',
            }}
        >
            {children}

            <span
                className="resize-handle"
                onMouseDown={(e) => startResize(e, column)}
            />
        </th>
    );

    const handleQtyChange = (index, value) => {
        if (value === '') {
            const updatedRows = rows.map((row, rowIndex) =>
                rowIndex === index ? { ...row, qty: '' } : row
            );

            syncRows(updatedRows);
            return;
        }

        const newQty = Number(value);
        const row = rows[index];

        const balQty = Number(row.balQty) || 0;

        if (!Number.isFinite(newQty) || newQty < 0) {
            toast.warning('Quantity cannot be less than 0.');
            return;
        }

        if (newQty > balQty) {
            toast.warning(
                'Enter a quantity less than or equal to the balance quantity.'
            );
            return;
        }

        const rate = getValidExchangeRate();
        const unitPrice = Number(row.baseAmount) || 0;

        const updatedRows = rows.map((currentRow, rowIndex) => {
            if (rowIndex !== index) return currentRow;

            const updatedRow = {
                ...currentRow,
                qty: newQty,
                baseQty: newQty || 0,
            };

            return calculateRowAmounts(updatedRow, unitPrice, rate);
        });

        syncRows(updatedRows);
    };

    const handleDateChange = (index, expDate) => {
        const updatedRows = rows.map((row, rowIndex) =>
            rowIndex === index ? { ...row, expDate } : row
        );

        syncRows(updatedRows);
    };

    const preventNegativeSign = (e) => {
        if (e.key === '-' || e.key === '+') {
            e.preventDefault();
        }
    };

    const handleFreeQTChange = (index, value) => {
        const newQty = value === '' ? '' : Number(value);

        if (newQty !== '' && (!Number.isFinite(newQty) || newQty < 0)) {
            toast.warning('Quantity cannot be less than 0.');
            return;
        }

        const updatedRows = rows.map((row, rowIndex) =>
            rowIndex === index ? { ...row, freeQty: newQty } : row
        );

        syncRows(updatedRows);
    };

    /*
     * Manual unit price edits reset the row's original price based on
     * the current exchange rate. Future rate changes then scale from it.
     */
    const handleUnitPriceChange = (index, value) => {
        if (value === '') {
            const updatedRows = rows.map((row, rowIndex) =>
                rowIndex === index
                    ? {
                          ...row,
                          baseAmount: '',
                          originalUnitPrice: 0,
                          calculatedAmount: 0,
                          vatAmount: '0',
                          amount: 0,
                      }
                    : row
            );

            syncRows(updatedRows);
            return;
        }

        const unitPrice = Number(value);

        if (!Number.isFinite(unitPrice) || unitPrice < 0) {
            return;
        }

        const rate = getValidExchangeRate();

        const updatedRows = rows.map((row, rowIndex) => {
            if (rowIndex !== index) return row;

            return calculateRowAmounts(
                {
                    ...row,
                    originalUnitPrice: unitPrice / rate,
                },
                unitPrice,
                rate
            );
        });

        syncRows(updatedRows);
    };

    const handleBatchNo = (index, value) => {
        const updatedRows = rows.map((row, rowIndex) =>
            rowIndex === index ? { ...row, batchNo: value } : row
        );

        syncRows(updatedRows);
    };

    const startResize = (e, column) => {
        e.preventDefault();

        const startX = e.clientX;
        const startWidth = columnWidths[column];

        const handleMouseMove = (event) => {
            const newWidth = Math.max(
                50,
                startWidth + (event.clientX - startX)
            );

            setColumnWidths((prev) => ({
                ...prev,
                [column]: newWidth,
            }));
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleQtyKeyDown = (e, index) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();

            const nextInput = document.getElementById(`qty-${index + 1}`);

            if (nextInput) {
                nextInput.focus();
                nextInput.select();
            }
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();

            const prevInput = document.getElementById(`qty-${index - 1}`);

            if (prevInput) {
                prevInput.focus();
                prevInput.select();
            }
        }
    };

    const handleInputChange = (index, fieldName, value) => {
        if (fieldName !== 'calculatedAmount') return;

        const calculatedAmount =
            value === '' ? 0 : Number(value) || 0;

        const updatedRows = rows.map((row, rowIndex) => {
            if (rowIndex !== index) return row;

            const vatRate = Number(row.vat) || 0;
            const vatAmount = calculatedAmount * vatRate / 100;

            return {
                ...row,
                calculatedAmount,
                vatAmount: roundAmount(vatAmount).toFixed(precision),
                amount: roundAmount(calculatedAmount + vatAmount),
            };
        });

        syncRows(updatedRows);
    };

    return (
        <div>
            <div className="row mb-3">
                <div className="col-md-4">
                    <label>Discount %</label>
                    <input
                        type="number"
                        className="form-control"
                        value={overallDiscount ?? 0}
                        disabled
                    />
                </div>

                <div className="col-md-4">
                    <label>Discount Amount</label>
                    <input
                        type="number"
                        className="form-control"
                        value={Number(overallDiscountAmount || 0).toFixed(precision)}
                        disabled
                    />
                </div>
            </div>

            <div
                className="table-container p-2 mb-5"
                style={{ overflowX: 'auto', height: 'auto' }}
            >
                <table>
                    <thead className="dynoTable">
                        <tr>
                            <ResizableHeader column="srNo">Sr no</ResizableHeader>
                            <ResizableHeader column="itemNo">Item No</ResizableHeader>
                            <ResizableHeader column="description">Description</ResizableHeader>
                            <ResizableHeader column="specification">Item Specs</ResizableHeader>
                            <ResizableHeader column="qty">Qty</ResizableHeader>
                            <ResizableHeader column="uom">Packing UOM</ResizableHeader>
                            <ResizableHeader column="inventoryQty">Inventory Qty</ResizableHeader>
                            <ResizableHeader column="balQty">Bal Qty</ResizableHeader>
                            <ResizableHeader column="freeQty">Free Qty</ResizableHeader>
                            <ResizableHeader column="unitPrice">Unit Price</ResizableHeader>
                            <ResizableHeader column="discount">Discount</ResizableHeader>
                            <ResizableHeader column="calculatedAmount">Calculated Amount</ResizableHeader>
                            <ResizableHeader column="vatDesc">VAT Desc</ResizableHeader>
                            <ResizableHeader column="vat">VAT %</ResizableHeader>
                            <ResizableHeader column="vatAmount">VAT Amount</ResizableHeader>
                            <ResizableHeader column="amount">Amount</ResizableHeader>
                            <ResizableHeader column="batchNo">Batch No</ResizableHeader>
                            <ResizableHeader column="expDate">Exp Date</ResizableHeader>
                        </tr>
                    </thead>

                    <tbody>
                        {rows.map((row, index) => (
                            <tr key={row.recordId ?? `${row.ItemNo}-${index}`}>
                                <td className="m-0 p-0" style={{ width: columnWidths.srNo, minWidth: columnWidths.srNo }}>
                                    <input className="p-2" type="text" value={index + 1} disabled style={{ width: '100%', boxSizing: 'border-box' }} />
                                </td>

                                <td className="m-0 p-0" style={{ width: columnWidths.itemNo, minWidth: columnWidths.itemNo }}>
                                    <input className="p-2" disabled value={row.ItemNo ?? ''} style={{ width: '100%', boxSizing: 'border-box' }} />
                                </td>

                                <td className="m-0 p-0" style={{ width: columnWidths.description, minWidth: columnWidths.description }}>
                                    <input className="p-2" disabled value={row.itemDescription ?? ''} style={{ width: '100%', boxSizing: 'border-box' }} />
                                </td>

                                <td className="m-0 p-0" style={{ width: columnWidths.specification, minWidth: columnWidths.specification }}>
                                    <input className="p-2" disabled value={row.specification ?? ''} style={{ width: '100%', boxSizing: 'border-box' }} />
                                </td>

                                <td className="m-0 p-0" style={{ width: columnWidths.qty, minWidth: columnWidths.qty }}>
                                    <input
                                        className="p-2"
                                        type="number"
                                        min="0"
                                        max={row.balQty}
                                        value={row.qty ?? ''}
                                        onWheel={(e) => e.target.blur()}
                                        onChange={(e) => handleQtyChange(index, e.target.value)}
                                        onKeyDown={(e) => handleQtyKeyDown(e, index)}
                                        id={`qty-${index}`}
                                        style={{ width: '100%', boxSizing: 'border-box' }}
                                    />
                                </td>

                                <td className="m-0 p-0" style={{ width: columnWidths.uom, minWidth: columnWidths.uom }}>
                                    <input className="p-2" disabled value={row.uom ?? ''} style={{ width: '100%', boxSizing: 'border-box' }} />
                                </td>

                                <td className="m-0 p-0" style={{ width: columnWidths.inventoryQty, minWidth: columnWidths.inventoryQty }}>
                                    <input className="p-2" disabled value={row.baseQty ?? 0} style={{ width: '100%', boxSizing: 'border-box' }} />
                                </td>

                                <td className="m-0 p-0" style={{ width: columnWidths.balQty, minWidth: columnWidths.balQty }}>
                                    <input className="p-2" disabled value={row.balQty ?? 0} style={{ width: '100%', boxSizing: 'border-box' }} />
                                </td>

                                <td className="m-0 p-0" style={{ width: columnWidths.freeQty, minWidth: columnWidths.freeQty }}>
                                    <input
                                        className="p-2"
                                        type="number"
                                        min="0"
                                        value={row.freeQty ?? 0}
                                        onWheel={(e) => e.target.blur()}
                                        onKeyDown={preventNegativeSign}
                                        onChange={(e) => handleFreeQTChange(index, e.target.value)}
                                        style={{ width: '100%', boxSizing: 'border-box' }}
                                    />
                                </td>

                                <td className="m-0 p-0" style={{ width: columnWidths.unitPrice, minWidth: columnWidths.unitPrice }}>
                                    <input
                                        className="p-2"
                                        type="number"
                                        min="0"
                                        step="any"
                                        value={row.baseAmount ?? ''}
                                        onWheel={(e) => e.target.blur()}
                                        onKeyDown={preventNegativeSign}
                                        onChange={(e) => handleUnitPriceChange(index, e.target.value)}
                                        style={{ width: '100%', boxSizing: 'border-box' }}
                                    />
                                </td>

                                <td className="m-0 p-0" style={{ width: columnWidths.discount, minWidth: columnWidths.discount }}>
                                    <input className="p-2" type="text" value={row.discount ?? 0} disabled style={{ width: '100%', boxSizing: 'border-box' }} />
                                </td>

                                <td className="m-0 p-0" style={{ width: columnWidths.calculatedAmount, minWidth: columnWidths.calculatedAmount }}>
                                    <input
                                        className="p-2"
                                        type="number"
                                        min="0"
                                        step="any"
                                        value={row.calculatedAmount ?? ''}
                                        onWheel={(e) => e.target.blur()}
                                        onKeyDown={preventNegativeSign}
                                        onChange={(e) => handleInputChange(index, 'calculatedAmount', e.target.value)}
                                        style={{ width: '100%', boxSizing: 'border-box' }}
                                    />
                                </td>

                                <td className="m-0 p-0" style={{ width: columnWidths.vatDesc, minWidth: columnWidths.vatDesc }}>
                                    <input className="p-2" type="text" value={row.vatDes ?? ''} disabled style={{ width: '100%', boxSizing: 'border-box' }} />
                                </td>

                                <td className="m-0 p-0" style={{ width: columnWidths.vat, minWidth: columnWidths.vat }}>
                                    <input className="p-2" type="text" value={`${row.vat || 0}%`} disabled style={{ width: '100%', boxSizing: 'border-box' }} />
                                </td>

                                <td className="m-0 p-0" style={{ width: columnWidths.vatAmount, minWidth: columnWidths.vatAmount }}>
                                    <input
                                        className="p-2"
                                        type="text"
                                        value={Number(row.vatAmount || 0).toFixed(precision)}
                                        disabled
                                        style={{ width: '100%', boxSizing: 'border-box' }}
                                    />
                                </td>

                                <td className="m-0 p-0" style={{ width: columnWidths.amount, minWidth: columnWidths.amount }}>
                                    <input
                                        className="p-2"
                                        disabled
                                        value={Number(row.amount || 0).toFixed(precision)}
                                        style={{ width: '100%', boxSizing: 'border-box' }}
                                    />
                                </td>

                                <td className="m-0 p-0" style={{ width: columnWidths.batchNo, minWidth: columnWidths.batchNo }}>
                                    <input
                                        className="p-2"
                                        type="text"
                                        value={row.batchNo ?? ''}
                                        onChange={(e) => handleBatchNo(index, e.target.value)}
                                        style={{ width: '100%', boxSizing: 'border-box' }}
                                    />
                                </td>

                                <td className="m-0 p-0" style={{ width: columnWidths.expDate, minWidth: columnWidths.expDate }}>
                                    <input
                                        className="p-2"
                                        type="date"
                                        value={row.expDate ? String(row.expDate).split('T')[0] : ''}
                                        onChange={(e) => handleDateChange(index, e.target.value)}
                                        style={{ width: '100%', boxSizing: 'border-box' }}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="d-flex justify-content-between boxs">
                <div className="d-flex gap-2">
                    <div className="totalhead center">Total Amount</div>
                    <div className="field">
                        {Number(calculatedTotal).toFixed(precision)}
                    </div>
                </div>

                <div>
                    <div className="totalhead center">_</div>
                </div>

                <div className="d-flex gap-2">
                    <div className="totalhead center">Total Discount</div>
                    <div className="field">
                        {Number(discountAmount || 0).toFixed(precision)}
                    </div>
                </div>

                <div>
                    <div className="totalhead center">=</div>
                </div>

                <div className="d-flex gap-2">
                    <div className="totalhead center">After Discount</div>
                    <div className="field">
                        {(calculatedTotal - Number(discountAmount || 0)).toFixed(precision)}
                    </div>
                </div>

                <div>
                    <div className="totalhead center">+</div>
                </div>

                <div className="d-flex gap-2">
                    <div className="totalhead center">Total Vat</div>
                    <div className="field">
                        {Number(vatAmount || 0).toFixed(precision)}
                    </div>
                </div>

                <div>
                    <div className="totalhead center">=</div>
                </div>

                <div className="d-flex gap-2">
                    <div className="totalhead center">Net Amount</div>
                    <div className="field">
                        {Number(grandTotal || 0).toFixed(precision)}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UpdateItemGridGRN;